import { ForecastModel, BaseForecastModelWithAvailability } from '@app/common/static-data/models';
import { ForecastContent } from './ForecastContent';
import { ForecastPanel } from './ForecastPanel';
import { PlottableForecast, VariableId, ModelId, Location } from '../types';

interface ForecastDesktopLayoutProps {
  // Data props
  loading: boolean;
  forecastData: Record<ModelId, PlottableForecast>;
  selectedParam: VariableId;
  selectedModels: ForecastModel[];
  endHour: number;
  location: Location;
  availableModels: BaseForecastModelWithAvailability[];

  // Favorites props
  favoriteVariables: Set<string>;
  favoriteModels: Set<string>;
  favoriteLocations: Location[];
  onFavoriteVariablesChange: (favorites: Set<string>) => void;
  onFavoriteModelsChange: (favorites: Set<string>) => void;
  addFavoriteLocation: (location: Location) => void;
  removeFavoriteLocation: (locationName: string) => void;
  isFavoriteLocation: (locationName: string) => boolean;

  // Model colors
  customModelColors: Record<string, string>;
  onModelColorChange: (modelId: string, color: string) => void;

  // Init times
  availableInitTimes: Record<string, string[]>;
  loadingInitTimes: Record<string, boolean>;
  openInitTimeSelectors: Record<string, boolean>;
  selectedInitTimes: Record<string, string>;
  onInitTimeChange: (modelId: string, initTime: string) => void;
  onInitTimeSelectorToggle: (modelId: string) => void;

  // Panel state props
  isPanelOpen: boolean;
  onSettingsClick: () => void;
  panelWidth: number;
  isResizing: boolean;
  expandedVariableSection: boolean;
  expandedLocationSection: boolean;
  expandedModelSection: boolean;
  expandedTimeRangeSection: boolean;
  setExpandedVariableSection: (expanded: boolean) => void;
  setExpandedLocationSection: (expanded: boolean) => void;
  setExpandedModelSection: (expanded: boolean) => void;
  setExpandedTimeRangeSection: (expanded: boolean) => void;

  // Handler props
  onParamChange: (param: VariableId) => void;
  onLocationChange: (location: Location) => void;
  onModelsChange: (models: ForecastModel[]) => void;
  onEndHourChange: (hour: number) => void;
  setPanelWidth: (width: number) => void;
  setIsResizing: (isResizing: boolean) => void;

  // Panel handlers
  handlePanelToggle: () => void;
  handleOpenPanelWithVariableExpansion: () => void;
  handleOpenPanelWithLocationExpansion: () => void;
  handleOpenPanelWithModelExpansion: () => void;
  handlePanelClose: () => void;
}

export function ForecastDesktopLayout({
  loading,
  forecastData,
  selectedParam,
  selectedModels,
  endHour,
  location,
  availableModels,
  favoriteVariables,
  favoriteModels,
  favoriteLocations,
  onFavoriteVariablesChange,
  onFavoriteModelsChange,
  addFavoriteLocation,
  removeFavoriteLocation,
  isFavoriteLocation,
  customModelColors,
  onModelColorChange,
  availableInitTimes,
  loadingInitTimes,
  openInitTimeSelectors,
  selectedInitTimes,
  onInitTimeChange,
  onInitTimeSelectorToggle,
  isPanelOpen,
  onSettingsClick,
  panelWidth,
  isResizing,
  expandedVariableSection,
  expandedLocationSection,
  expandedModelSection,
  expandedTimeRangeSection,
  setExpandedVariableSection,
  setExpandedLocationSection,
  setExpandedModelSection,
  setExpandedTimeRangeSection,
  onParamChange,
  onLocationChange,
  onModelsChange,
  onEndHourChange,
  setPanelWidth,
  setIsResizing,
  handleOpenPanelWithVariableExpansion,
  handleOpenPanelWithLocationExpansion,
  handleOpenPanelWithModelExpansion,
  handlePanelClose,
}: ForecastDesktopLayoutProps) {
  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      <div className="flex flex-1 min-h-0 relative w-full">
        {/* Main content area */}
        <div
          className={`flex flex-col min-h-0 relative ${
            isResizing ? '' : 'transition-all duration-300 ease-in-out'
          }`}
          style={{
            width: isPanelOpen ? `calc(100% - ${panelWidth}px)` : '100%',
          }}
        >
          <div className="flex-1 overflow-y-auto">
            <ForecastContent
              loading={loading}
              forecastData={forecastData}
              selectedParam={selectedParam}
              selectedModels={selectedModels}
              endHour={endHour}
              onParamChange={onParamChange}
              favoriteVariables={favoriteVariables}
              location={location}
              onLocationChange={onLocationChange}
              favoriteLocations={favoriteLocations}
              onOpenPanel={handleOpenPanelWithVariableExpansion}
              onOpenPanelWithLocation={handleOpenPanelWithLocationExpansion}
              onOpenPanelWithModel={handleOpenPanelWithModelExpansion}
              availableModels={availableModels}
              favoriteModels={favoriteModels}
              onModelsChange={onModelsChange}
              customModelColors={customModelColors}
              isPanelOpen={isPanelOpen}
              onSettingsClick={onSettingsClick}
            />
          </div>
        </div>

        {/* Desktop Side Panel Container */}
        <div
          className={`h-full flex-shrink-0 overflow-hidden ${
            isResizing ? '' : 'transition-all duration-300 ease-in-out'
          }`}
          style={{
            width: isPanelOpen ? `${panelWidth}px` : '0px',
          }}
        >
          <ForecastPanel
            isOpen={isPanelOpen}
            onClose={handlePanelClose}
            onWidthChange={setPanelWidth}
            onResizeChange={setIsResizing}
            selectedParam={selectedParam}
            onParamChange={onParamChange}
            selectedModels={selectedModels}
            onModelsChange={onModelsChange}
            endHour={endHour}
            onEndHourChange={onEndHourChange}
            availableModels={availableModels}
            loading={loading}
            favoriteVariables={favoriteVariables}
            favoriteModels={favoriteModels}
            onFavoriteVariablesChange={onFavoriteVariablesChange}
            onFavoriteModelsChange={onFavoriteModelsChange}
            location={location}
            onLocationChange={onLocationChange}
            favoriteLocations={favoriteLocations}
            addFavoriteLocation={addFavoriteLocation}
            removeFavoriteLocation={removeFavoriteLocation}
            isFavoriteLocation={isFavoriteLocation}
            customModelColors={customModelColors}
            onModelColorChange={onModelColorChange}
            availableInitTimes={availableInitTimes}
            loadingInitTimes={loadingInitTimes}
            openInitTimeSelectors={openInitTimeSelectors}
            selectedInitTimes={selectedInitTimes}
            onInitTimeChange={onInitTimeChange}
            onInitTimeSelectorToggle={onInitTimeSelectorToggle}
            expandedVariableSection={expandedVariableSection}
            onExpandedVariableSectionChange={setExpandedVariableSection}
            expandedLocationSection={expandedLocationSection}
            onExpandedLocationSectionChange={setExpandedLocationSection}
            expandedModelSection={expandedModelSection}
            onExpandedModelSectionChange={setExpandedModelSection}
            expandedTimeRangeSection={expandedTimeRangeSection}
            onExpandedTimeRangeSectionChange={setExpandedTimeRangeSection}
          />
        </div>
      </div>
    </div>
  );
}
